import { format } from 'date-fns';
import { toast } from 'react-toastify';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { PlayArrow as PlayIcon, CheckCircle as CheckIcon, Error as ErrorIcon, Pause as PauseIcon, Stop as StopIcon, LocalOffer as TagIcon, Add as AddIcon } from '@mui/icons-material';
import { Box, Button, Typography, IconButton, Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Popover, List, ListItem, ListItemText, Grid, Paper, Card, CardContent } from '@mui/material';

// Import our new components
import ExecutionMonitor from './ExecutionMonitor';
import TagsManager from '../../components/Tags/TagsManager';
import { getSingleWorkflow, getWorkflowExecutions, deleteWorkflow, cloneWorkflow, executeWorkflow, updateWorkflow } from '../../store/Workflows';
import { getAllObjectTags, bulkUpdateObjectTags } from '../../store/Objects';

const WorkflowDetail = ({ workflowId, setViewMode }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { id: routeId } = useParams();
  const id = workflowId || routeId; // Support both prop and route param
  const { singleWorkflow, singleWorkflowLoading, executions, executionsLoading, executionsTotal } = useSelector(state => state.Workflows);

  const [activeTab, setActiveTab] = useState(0);
  const [nodeLogs, setNodeLogs] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [executionInput, setExecutionInput] = useState('{}');
  const [executionInputError, setExecutionInputError] = useState('');
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const tagsOpen = Boolean(tagsAnchorEl);

  // Infinite scroll state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const previousWorkflowIdRef = useRef(id);

  // Reset when workflow changes
  useEffect(() => {
    if (previousWorkflowIdRef.current !== id) {
      setCurrentPage(1);
      setHasMore(true);
      setInitialLoadDone(false);
      previousWorkflowIdRef.current = id;
    }
  }, [id]);

  // Initial data fetch - simple and clean
  useEffect(() => {
    if (id && !initialLoadDone && !executionsLoading) {

      // Fetch workflow details if needed
      if (!singleWorkflow || singleWorkflow._id !== id) {
        dispatch(getSingleWorkflow(id));
      }

      // Fetch first page
      dispatch(getWorkflowExecutions({ workflowId: id, page: 1, limit: 20 }));
      setInitialLoadDone(true);
    }
  }, [id, initialLoadDone, executionsLoading, singleWorkflow, dispatch]);

  // Update hasMore when data changes
  useEffect(() => {
    if (executionsTotal > 0) {
      const hasMoreData = executions.length < executionsTotal;
      setHasMore(hasMoreData);
    }
  }, [executions.length, executionsTotal, currentPage]);

  // Infinite scroll observer - simple and clean
  useEffect(() => {
    // Must have: sentinel element, data loaded, and more data to load
    if (!loadMoreRef.current || !initialLoadDone || !hasMore || executionsLoading || isLoadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !executionsLoading && !isLoadingMore) {
          const nextPage = currentPage + 1;
          setIsLoadingMore(true);
          setCurrentPage(nextPage);
          dispatch(getWorkflowExecutions({
            workflowId: id,
            page: nextPage,
            limit: 20,
            append: true
          }));
        }
      },
      {
        threshold: 0,
        rootMargin: '50px'
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [dispatch, id, currentPage, hasMore, executionsLoading, initialLoadDone, isLoadingMore]);

  // Reset isLoadingMore when data finishes loading
  useEffect(() => { 
    if (!executionsLoading && isLoadingMore) {
      setIsLoadingMore(false);  
    }
  }, [executionsLoading, isLoadingMore]);

  const handleEdit = () => {
    history.push(`/agent/workflows/${id}/edit`);
  };

  const handleClone = () => {
    const newName = prompt('Enter name for cloned workflow:', `${singleWorkflow.name} (Copy)`);
    if (newName) {
      dispatch(cloneWorkflow({ id, name: newName }));
    }
  };

  const handleNodeProgress = (log) => {
    updateNodeLog(log.nodeId, { ...nodeLogs[log.nodeId], ...log });
  };

  const handleTagsClick = (event) => {
    setTagsAnchorEl(event.currentTarget);
    dispatch(getAllObjectTags({ objectType: 'workflows' }));
  };

  const handleTagsClose = () => {
    setTagsAnchorEl(null);
  };

  const handleTagsApplied = (mergedTags) => {
    // Immediately update the local state with merged tags
    if (singleWorkflow && mergedTags) {
      const updatedWorkflow = {
        ...singleWorkflow,
        tags: mergedTags
      };
      // Update Redux state immediately for single workflow
      dispatch({ type: 'workflows/SET_SINGLE_WORKFLOW', payload: updatedWorkflow });

      // Also update the workflow in the main list
      dispatch({ type: 'workflows/UPDATE_WORKFLOW_IN_LIST', payload: updatedWorkflow });
    }
    // Also refresh from server to ensure consistency
    dispatch(getSingleWorkflow(id));
    handleTagsClose();
  };

  // Tag removal functionality has been disabled
  // const handleRemoveTag = (tagToRemove) => {
  //   const updatedTags = singleWorkflow.tags.filter(tag => tag.name !== tagToRemove.name);
  //   // ... removal logic
  // };

  const handleDelete = () => {
    dispatch(deleteWorkflow(id));
    setDeleteDialogOpen(false);
  };

  const handleExecute = () => {
    try {
      const parsedInput = JSON.parse(executionInput);
      toast.success('Workflow execution started');
      dispatch(executeWorkflow({
        id,
        input: parsedInput,
        successCallback: () => {
          setExecuteDialogOpen(false);
          setExecutionInput('{}');
          // Refresh executions from page 1
          setCurrentPage(1);
          dispatch(getWorkflowExecutions({ workflowId: id, page: 1, limit: 20 }));
        },
        failCallback: () => toast.error('Failed to execute workflow')
      }));
    } catch (error) { setExecutionInputError('Invalid JSON format') }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: 'success',
      inactive: 'default',
      completed: 'success',
      running: 'info',
      failed: 'error'
    };
    return statusColors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckIcon fontSize="small" />;
      case 'running':
        return <CircularProgress size={16} />;
      case 'failed':
        return <ErrorIcon fontSize="small" />;
      case 'paused':
        return <PauseIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (singleWorkflowLoading || !singleWorkflow || !singleWorkflow._id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }


  const columns = [
    {
      name: "Execution",
      selector: (row) => row.executionNumber || row._id.slice(-6),
      sortable: true,
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {getStatusIcon(row.status)}
          <span>Execution #{row.executionNumber || row._id.slice(-6)}</span>
        </div>
      ),
    },
    {
      name: "Date & Time",
      selector: (row) => row.startTime,
      sortable: true,
      cell: (row) =>
        row.startTime
          ? format(new Date(row.startTime), "MMM d, HH:mm:ss")
          : "N/A",
    },

    {
      name: "Status & Duration",
      selector: (row) => row.status,
      cell: (row) => (
        <div>
          <span style={{
            color: row.status === 'completed' ? '#28a745'
                 : row.status === 'failed' || row.status === 'error' ? '#dc3545'
                 : row.status === 'running' ? '#007bff'
                 : '#6c757d',
            textTransform: 'capitalize',
            fontWeight: 500
          }}>
            {row.status}
          </span>
          {' in '}
          {row.executionTime
            ? `${row.executionTime}ms`
            : row.endTime && row.startTime
              ? `${Math.round((new Date(row.endTime) - new Date(row.startTime)) / 1000)}s`
              : row.status === 'running'
                ? 'progress...'
                : 'N/A'
          }
        </div>
      ),
    },
    {
      name: "Action",
      cell: (row) =>
        row.status === "running" ? (
          <IconButton edge="end">
            <StopIcon />
          </IconButton>
        ) : null,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // table stats
  const isPaginated = executions.length > 100;
  const headerScrollHeight = isPaginated ? 'calc(100vh - 189px)' : 'calc(100vh - 161px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight contact-table';

  return (
    <>
      <div className='executions-sec'>
        <div className='executions-left-sidebar'>
          <div className='executions-bottom-area'>
            <div className="table-container filters-table workflow-table">
              <div className='header-table-filters advanced-table-filters'>
                <div className='left-area'>
                  <h4>Executions</h4>
                  {/* Tags Display */}
                  {singleWorkflow.tags && singleWorkflow.tags.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <TagIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                      {singleWorkflow.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.name}
                          size="small"
                          sx={{
                            backgroundColor: tag.color || '#6B7280',
                            color: 'white',
                            height: '20px',
                            fontSize: '11px',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                      <IconButton
                        size="small"
                        onClick={handleTagsClick}
                        sx={{ padding: 0.5 }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                  {/* {(!singleWorkflow.tags || singleWorkflow.tags.length === 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={<TagIcon sx={{ fontSize: 16 }} />}
                        onClick={handleTagsClick}
                        sx={{ fontSize: '12px', textTransform: 'none' }}
                      >
                        Add Tags
                      </Button>
                    </Box>
                  )} */}
                </div>
                <div className='right-deals-filtes'>
                  <button className='refresh-btns' onClick={() => {
                    const now = Date.now();
                    const timeSinceLastFetch = now - lastFetchTime;

                    if (!executionsLoading && timeSinceLastFetch > 1000) {
                      setLastFetchTime(now);
                      setCurrentPage(1);
                      dispatch(getWorkflowExecutions({ workflowId: id, page: 1, limit: 20 }));
                    }
                  }} disabled={executionsLoading || (Date.now() - lastFetchTime) < 1000}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.9124 16.8149C10.6649 16.8149 10.4324 16.6499 10.3649 16.3949C10.2824 16.0949 10.4624 15.7874 10.7699 15.7049C13.8149 14.9024 15.9374 12.1424 15.9374 8.99242C15.9374 5.16742 12.8249 2.05492 8.99988 2.05492C5.75238 2.05492 3.62238 3.95242 2.62488 5.09992H4.82988C5.13738 5.09992 5.39238 5.35492 5.39238 5.66242C5.39238 5.96992 5.14488 6.23242 4.82988 6.23242H1.50738C1.46988 6.23242 1.40238 6.22492 1.34988 6.20992C1.28238 6.18742 1.22238 6.15742 1.16988 6.11992C1.10238 6.07492 1.04988 6.01492 1.01238 5.94742C0.974878 5.87992 0.944878 5.79742 0.937378 5.71492C0.937378 5.69242 0.937378 5.67742 0.937378 5.65492V2.24992C0.937378 1.94242 1.19238 1.68742 1.49988 1.68742C1.80738 1.68742 2.06238 1.94242 2.06238 2.24992V4.04242C3.28488 2.72992 5.58738 0.937424 8.99988 0.937424C13.4474 0.937424 17.0624 4.55242 17.0624 8.99992C17.0624 12.6599 14.5949 15.8699 11.0549 16.7999C11.0099 16.8074 10.9574 16.8149 10.9124 16.8149Z" fill="#4B5563" />
                      <path d="M8.46738 17.0475C8.45238 17.0475 8.43738 17.04 8.42988 17.04C7.61988 16.9875 6.82488 16.8075 6.07488 16.515C5.85738 16.4325 5.70738 16.215 5.71488 15.9825C5.71488 15.915 5.72988 15.8475 5.75238 15.7875C5.86488 15.5025 6.20238 15.36 6.47988 15.465C7.13238 15.72 7.81488 15.87 8.50488 15.9225C8.79738 15.9375 9.02988 16.1925 9.02988 16.4925L9.02238 16.5225C9.00738 16.815 8.75988 17.0475 8.46738 17.0475ZM4.33488 15.435C4.20738 15.435 4.08738 15.39 3.98238 15.315C3.35238 14.805 2.79738 14.2125 2.34738 13.5525C2.27988 13.455 2.24238 13.35 2.24238 13.2375C2.24238 13.05 2.33238 12.8775 2.48988 12.7725C2.73738 12.6 3.09738 12.6675 3.26988 12.9075C3.26988 12.915 3.26988 12.915 3.26988 12.915C3.27738 12.9225 3.28488 12.9375 3.29238 12.945C3.68238 13.5075 4.15488 14.01 4.68738 14.43C4.81488 14.535 4.89738 14.6925 4.89738 14.865C4.89738 14.9925 4.85988 15.12 4.77738 15.225C4.66488 15.36 4.50738 15.435 4.33488 15.435ZM1.82988 11.775C1.58238 11.775 1.36488 11.6175 1.29738 11.385C1.05738 10.6125 0.937378 9.81 0.937378 9V8.9925C0.944878 8.685 1.19238 8.4375 1.49988 8.4375C1.80738 8.4375 2.06238 8.6925 2.06238 9C2.06238 9.705 2.16738 10.395 2.36988 11.0475C2.38488 11.1075 2.39238 11.16 2.39238 11.22C2.39238 11.46 2.23488 11.6775 1.99488 11.7525C1.94238 11.7675 1.88988 11.775 1.82988 11.775Z" fill="#4B5563" />
                    </svg>
                  </button>
                  {/* <button className='refresh-btns'>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.38161 13.2125C6.10161 13.2125 5.82744 13.1425 5.57078 13.0025C5.05744 12.7167 4.74828 12.1975 4.74828 11.6142V8.5225C4.74828 8.23083 4.55578 7.79333 4.37494 7.57166L2.19328 5.26167C1.82578 4.89417 1.54578 4.26417 1.54578 3.79167V2.45C1.54578 1.51667 2.25161 0.787498 3.14994 0.787498H10.8499C11.7366 0.787498 12.4541 1.505 12.4541 2.39167V3.675C12.4541 4.2875 12.0866 4.98167 11.7424 5.32583L9.21661 7.56C8.97161 7.76416 8.77911 8.21333 8.77911 8.575V11.0833C8.77911 11.6025 8.45244 12.2033 8.04411 12.4483L7.23911 12.9675C6.97661 13.1308 6.67911 13.2125 6.38161 13.2125ZM3.14994 1.6625C2.74161 1.6625 2.42078 2.00666 2.42078 2.45V3.79167C2.42078 4.0075 2.59578 4.4275 2.81744 4.64916L5.03994 6.98833C5.33744 7.35583 5.62911 7.96833 5.62911 8.51666V11.6083C5.62911 11.9875 5.89161 12.1742 6.00244 12.2325C6.24744 12.3667 6.54494 12.3667 6.77244 12.2267L7.58328 11.7075C7.74661 11.6083 7.90994 11.2933 7.90994 11.0833V8.575C7.90994 7.95083 8.21328 7.2625 8.65078 6.895L11.1474 4.68417C11.3458 4.48583 11.5849 4.01333 11.5849 3.66917V2.39167C11.5849 1.98917 11.2583 1.6625 10.8558 1.6625H3.14994Z" fill="#4B5563" />
                      <path d="M3.49995 6.27083C3.41829 6.27083 3.34245 6.2475 3.26662 6.20667C3.06245 6.07833 2.99829 5.80417 3.12662 5.6L6.00245 0.991665C6.13079 0.787499 6.39912 0.723332 6.60329 0.851665C6.80745 0.979999 6.87162 1.24833 6.74329 1.4525L3.86745 6.06083C3.78579 6.195 3.64579 6.27083 3.49995 6.27083Z" fill="#4B5563" />
                    </svg>
                  </button> */}
                  <button className='refresh-btns' onClick={() => setViewMode("visual")} >
                    <svg width="12px" height="12px" viewBox="0 0 56.326 56.326" xmlns="http://www.w3.org/2000/svg" fill="var(--primary-color)">
                      <g id="SVGRepo_bgCarrier" stroke-width="0" />
                      <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                      <g id="SVGRepo_iconCarrier"> <path id="Path_14" data-name="Path 14" d="M477.613,422.087l25.6-25.6a1.5,1.5,0,0,0-2.122-2.121l-25.6,25.6-25.6-25.6a1.5,1.5,0,1,0-2.121,2.121l25.6,25.6-25.6,25.6a1.5,1.5,0,0,0,2.121,2.122l25.6-25.6,25.6,25.6a1.5,1.5,0,0,0,2.122-2.122Z" transform="translate(-447.328 -393.924)" fill="var(--primary-color)" /> </g>
                    </svg>
                  </button>
                </div>
              </div>
              <div className='executions-list-sec'>
                {executionsLoading && executions.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : executions.length > 0 ? (
                  <ul>
                    {executions.map((execution) => (
                      <li
                        key={execution._id}
                        onClick={() => {
                          setSelectedExecution(execution);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className='execution-list-box'>
                          <div className='execution-list-item'>
                            <h4>
                              <i className='icon'>{getStatusIcon(execution.status)}</i>
                              Execution #{execution.executionNumber || execution._id.slice(-6)}
                            </h4>
                          </div>
                          <div className='execution-list-item-inner'>
                            <p>{execution.startTime ? format(new Date(execution.startTime), 'MMM d, HH:mm:ss') : 'N/A'}</p>
                            <p>
                              <span style={{
                                color: execution.status === 'completed' ? '#28a745'
                                     : execution.status === 'failed' || execution.status === 'error' ? '#dc3545'
                                     : execution.status === 'running' ? '#007bff'
                                     : '#6c757d',
                                textTransform: 'capitalize',
                                fontWeight: 500
                              }}>
                                {execution.status}
                              </span>
                              {' in '}
                              {execution.executionTime
                                ? `${execution.executionTime}ms`
                                : execution.endTime && execution.startTime
                                  ? `${Math.round((new Date(execution.endTime) - new Date(execution.startTime)) / 1000)}s`
                                  : execution.status === 'running'
                                    ? 'progress...'
                                    : 'N/A'
                              }
                            </p>
                          </div>
                          <div className='right-acction-area'>
                            {execution.status === 'running' && (
                              <button className='refresh-btns'>
                                <StopIcon />
                              </button>
                            )}
                            {/* refresh button commented because not functional for now  */}
                            {/* <button className='refresh-btns'>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.38161 13.2125C6.10161 13.2125 5.82744 13.1425 5.57078 13.0025C5.05744 12.7167 4.74828 12.1975 4.74828 11.6142V8.5225C4.74828 8.23083 4.55578 7.79333 4.37494 7.57166L2.19328 5.26167C1.82578 4.89417 1.54578 4.26417 1.54578 3.79167V2.45C1.54578 1.51667 2.25161 0.787498 3.14994 0.787498H10.8499C11.7366 0.787498 12.4541 1.505 12.4541 2.39167V3.675C12.4541 4.2875 12.0866 4.98167 11.7424 5.32583L9.21661 7.56C8.97161 7.76416 8.77911 8.21333 8.77911 8.575V11.0833C8.77911 11.6025 8.45244 12.2033 8.04411 12.4483L7.23911 12.9675C6.97661 13.1308 6.67911 13.2125 6.38161 13.2125ZM3.14994 1.6625C2.74161 1.6625 2.42078 2.00666 2.42078 2.45V3.79167C2.42078 4.0075 2.59578 4.4275 2.81744 4.64916L5.03994 6.98833C5.33744 7.35583 5.62911 7.96833 5.62911 8.51666V11.6083C5.62911 11.9875 5.89161 12.1742 6.00244 12.2325C6.24744 12.3667 6.54494 12.3667 6.77244 12.2267L7.58328 11.7075C7.74661 11.6083 7.90994 11.2933 7.90994 11.0833V8.575C7.90994 7.95083 8.21328 7.2625 8.65078 6.895L11.1474 4.68417C11.3458 4.48583 11.5849 4.01333 11.5849 3.66917V2.39167C11.5849 1.98917 11.2583 1.6625 10.8558 1.6625H3.14994Z" fill="#4B5563" />
                                <path d="M3.49995 6.27083C3.41829 6.27083 3.34245 6.2475 3.26662 6.20667C3.06245 6.07833 2.99829 5.80417 3.12662 5.6L6.00245 0.991665C6.13079 0.787499 6.39912 0.723332 6.60329 0.851665C6.80745 0.979999 6.87162 1.24833 6.74329 1.4525L3.86745 6.06083C3.78579 6.195 3.64579 6.27083 3.49995 6.27083Z" fill="#4B5563" />
                              </svg>
                            </button> */}
                          </div>
                        </div>
                      </li>
                    ))}
                    {/* Sentinel element INSIDE ul for infinite scroll */}
                    {hasMore && executions.length > 0 && !isLoadingMore && (
                      <li
                        ref={loadMoreRef}
                        style={{
                          listStyle: 'none',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '10px'
                        }}
                      >
                        <CircularProgress size={16} style={{ color: '#9ca3af' }} />
                      </li>
                    )}
                    {/* Loading indicator when fetching more - INSIDE ul */}
                    {isLoadingMore && (
                      <li style={{ listStyle: 'none', padding: '10px', textAlign: 'center' }}>
                        <CircularProgress size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        <span style={{ color: '#6b7280', fontSize: '13px', verticalAlign: 'middle' }}>
                          Loading more...
                        </span>
                      </li>
                    )}
                  </ul>
                ) : (
                  <Alert severity="info">No executions found for this workflow</Alert>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className='executions-right-sec'>
          {selectedExecution ? (
            <ExecutionMonitor
              execution={selectedExecution}
              workflowData={singleWorkflow}
            />
          ) : (
            <div className='no-selection'>
              <div className='img-box'>
                <img alt="..." src={require('../../static/img/not-found.png')} />
              </div>
              Select an execution to view details
            </div>
          )}
        </div>
      </div>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => history.push('/agent/workflows')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">{singleWorkflow.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              Created {singleWorkflow.createdAt ? format(new Date(singleWorkflow.createdAt), 'MMM d, yyyy') : 'N/A'}
            </Typography>
          </Box>
          <Chip
            label={singleWorkflow.status}
            color={getStatusColor(singleWorkflow.status)}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PlayIcon />}
            onClick={() => setExecuteDialogOpen(true)}
            disabled={singleWorkflow.status !== 'active'}
          >
            Execute
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={handleClone}
          >
            Clone
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Box> */}

        {/* Tabs */}
        {/* <Paper sx={{ mb: 3 }}>
        <div className='excation-tabs'>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Executions" />
            <Tab label="Configuration" />
          </Tabs>
        </div>
      </Paper> */}

        {/* Tab Content */}
        {/* {activeTab === 0 && (
        <>
          <div className='workflow-overview-sec'>
            <div className='row'>
              <div className='col-lg-6 col-md-12'>
                <div className='workflow-overview-box'>
                  <h4>Workflow Details</h4>
                  {singleWorkflow.description && (
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {singleWorkflow.description}
                    </Typography>
                  )}
                  <div className='workflow-overview-info'>
                    <h5>Nodes</h5>
                    <List>
                      {singleWorkflow.nodes?.map((node, index) => (
                        <ListItem key={node.id || index}>
                          <ListItemText
                            primary={node.data?.label || node.type}
                            secondary={`Type: ${node.type}`}
                          />
                        </ListItem>
                      ))}
                      {(!singleWorkflow.nodes || singleWorkflow.nodes.length === 0) && (
                        <Typography variant="body2" color="textSecondary">
                          No nodes configured
                        </Typography>
                      )}
                    </List>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Grid container spacing={3}>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Workflow Info</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Type</Typography>
                    <Typography variant="body1">{singleWorkflow.type || 'custom'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Status</Typography>
                    <Typography variant="body1">
                      <Chip
                        label={singleWorkflow.status}
                        color={getStatusColor(singleWorkflow.status)}
                        size="small"
                      />
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Last Modified</Typography>
                    <Typography variant="body1">
                      {singleWorkflow.updatedAt ? format(new Date(singleWorkflow.updatedAt), 'MMM d, yyyy h:mm a') : 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Total Executions</Typography>
                    <Typography variant="body1">{singleWorkflow.executionCount || 0}</Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )} */}

        {/* {activeTab === 1 && (
        
      )} */}

        {/* {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>Workflow Configuration</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>Execution Settings</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Max Execution Time</Typography>
                      <Typography variant="body1">
                        {singleWorkflow.settings?.maxExecutionTime
                          ? `${singleWorkflow.settings.maxExecutionTime / 1000}s`
                          : 'Not set'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Error Handling</Typography>
                      <Typography variant="body1">
                        {singleWorkflow.settings?.errorHandling || 'Stop on error'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>Retry Policy</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Max Retries</Typography>
                      <Typography variant="body1">
                        {singleWorkflow.settings?.retryPolicy?.maxRetries || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Retry Delay</Typography>
                      <Typography variant="body1">
                        {singleWorkflow.settings?.retryPolicy?.retryDelay
                          ? `${singleWorkflow.settings.retryPolicy.retryDelay / 1000}s`
                          : 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )} */}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Workflow</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{singleWorkflow.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tags Popover */}
        <Popover
          open={tagsOpen}
          anchorEl={tagsAnchorEl}
          onClose={handleTagsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <TagsManager
            objectType="workflows"
            mode="select"
            selectedObjects={[id]}
            selectedTags={singleWorkflow.tags || []}
            onTagsApplied={handleTagsApplied}
          />
        </Popover>

        {/* Execute Dialog */}
        <Dialog open={executeDialogOpen} onClose={() => setExecuteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Execute Workflow</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Provide input data for the workflow execution (JSON format):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={executionInput}
              onChange={(e) => {
                setExecutionInput(e.target.value);
                setExecutionInputError('');
              }}
              error={!!executionInputError}
              helperText={executionInputError}
              placeholder='{"key": "value"}'
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setExecuteDialogOpen(false);
              setExecutionInput('{}');
              setExecutionInputError('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleExecute} variant="contained" startIcon={<PlayIcon />}>
              Execute
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};



const tableStyles = `
          .table-salesai input[type="checkbox"] {
            width: 13px;
          height: 13px;
          cursor: pointer;
          accent-color: #cbd6e2;
          transform: scale(1);
          opacity: 0.45;
  }

          .table-salesai input[type="checkbox"]:checked {
            opacity: 1;
          accent-color: var(--primary-color);
  }

          .rdt_TableRow {
            transition: background-color 0.15s ease;
  }

          .rdt_TableRow:hover {
            background - color: #f5f5f5;
  }

          .table-container {
            position: relative;
  }
          `;

export default WorkflowDetail;